import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, ErrorHandler, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationError, Router } from '@angular/router';

import { Logger } from './logger.service';

/**
 * Custom error handler that integrates with the Logger
 * to capture and log all unhandled errors in the application.
 */
@Injectable({
  providedIn: 'root',
})
export class LoggingErrorHandler implements ErrorHandler {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  constructor() {
    // Listen for navigation errors
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationError) {
        this.handleError(event.error);
      }
    });
  }

  /**
   * Handles all uncaught errors in the application
   */
  handleError(error: Error): void {
    // Extract meaningful information from the error
    const { message, name, status, statusText, url, stack } = this.extractErrorInfo(error);

    // Prepare data array with relevant information
    const data = {
      name,
      stack,
      status,
      statusText,
      timestamp: new Date(performance.timeOrigin + performance.now()).toISOString(),
      url,
      userAgent: window.navigator.userAgent,
    };

    // Create error message
    let errorMessage = `Uncaught error: ${message}`;

    // Add HTTP status if available
    if (status) {
      errorMessage = `HTTP Error ${status}: ${message}`;
    }

    // Log using our logging service
    Logger.error(errorMessage, { data, error, source: 'ErrorHandler' });
  }

  /**
   * Extracts useful information from different error types
   */
  private extractErrorInfo(error: unknown): {
    message: string;
    name: string;
    stack?: string;
    status?: number;
    statusText?: string;
    url?: string;
  } {
    // Default values
    let message = 'Unknown error';
    let name = 'Error';
    let stack: string | undefined;
    let status: number | undefined;
    let statusText: string | undefined;
    let url: string | undefined;

    // Handle Error objects
    if (error instanceof Error) {
      message = error.message;
      name = error.name;
      stack = error.stack;
    }

    // Handle HTTP errors
    if (error instanceof HttpErrorResponse) {
      message = error.message;
      name = 'HttpErrorResponse';
      status = error.status;
      statusText = error.statusText;
      url = error.url || undefined;
    }

    // Handle string errors
    if (typeof error === 'string') {
      message = error;
    }

    // Handle plain objects or other types
    if (error && typeof error === 'object' && !Array.isArray(error)) {
      // Extract message if available
      if ('message' in error && typeof error.message === 'string') {
        message = error.message;
      }

      // Extract name if available
      if ('name' in error && typeof error.name === 'string') {
        name = error.name;
      }
    }

    return { message, name, stack, status, statusText, url };
  }
}
